import { fetchApi } from "~/lib/api";
import { UMAMI_DISABLED_KEY } from "~/lib/constants";
import type {
  AdminGetParamsSessions,
  AdminGetParamsUserActivity,
  AdminGetTopSearchQueriesParams,
  AdminSessionSummary,
  AdminSessionsResponse,
  AdminTopSearchQuery,
  UserActivityResponse,
} from "~/types";
import { AnalyticsEvent, AnalyticsEvents } from "./analyticsEvents";
import { serializeFilterParams } from "./utils";
export { AnalyticsEvents } from "./analyticsEvents";
export type { AnalyticsEvent } from "./analyticsEvents";

export const analyticsService = {
  /**
   * Get unified customer activity (pageviews and events) from Umami
   */
  async getUserActivity(
    userId: string,
    options?: AdminGetParamsUserActivity,
  ): Promise<UserActivityResponse> {
    const query = serializeFilterParams(options || {});

    const endpoint = `/admin/analytics/users/${userId}${query}`;
    return fetchApi<UserActivityResponse>(endpoint);
  },

  /**
   * Get all website sessions
   */
  async getSessions(
    options: AdminGetParamsSessions = {},
  ): Promise<AdminSessionsResponse> {
    const query = serializeFilterParams(options);

    const endpoint = `/admin/analytics/sessions${query}`;
    return fetchApi<AdminSessionsResponse>(endpoint);
  },

  /**
   * Get session details by session ID
   */
  async getSessionById(sessionId: string): Promise<AdminSessionSummary> {
    const endpoint = `/admin/analytics/sessions/${sessionId}`;
    return fetchApi<AdminSessionSummary>(endpoint);
  },

  /**
   * Get session activity by session ID
   */
  async getSessionActivity(
    sessionId: string,
    options: AdminGetParamsUserActivity = {},
  ): Promise<UserActivityResponse> {
    const query = serializeFilterParams(options);

    const endpoint = `/admin/analytics/sessions/${sessionId}/activity${query}`;
    return fetchApi<UserActivityResponse>(endpoint);
  },

  /**
   * Get top search queries from Google Search Console (admin)
   */
  async getTopSearchQueries(options: AdminGetTopSearchQueriesParams = {}): Promise<AdminTopSearchQuery[]> {
    const query = serializeFilterParams(options);
    const endpoint = `/admin/analytics/cautari${query}`;
    return fetchApi<AdminTopSearchQuery[]>(endpoint);
  },
};

/**
 * Umami Analytics Helper
 *
 * Client-side event tracking using Umami's window.umami API
 */

declare global {
  interface Window {
    umami?: {
      track: (eventName?: string, eventData?: Record<string, unknown>) => void;
      identify: (
        distinctId: string,
        userData: { userId?: string; email?: string; name?: string },
      ) => void;
    };
    umamiBeforeSendHandler?: (
      type: string,
      payload: {
        name?: string;
        data?: Record<string, unknown>;
        id?: string;
        url?: string;
      },
    ) =>
      | {
        name?: string;
        data?: Record<string, unknown>;
        id?: string;
        url?: string;
      }
      | false;
  }
}

// Callbacks to notify when a tracking event occurs
type TrackEventCallback = (eventName?: string) => void;
const trackEventCallbacks: TrackEventCallback[] = [];

/**
 * Register a callback to be notified when any tracking event occurs
 * @param callback - Function to call when an event is tracked
 * @returns Unregister function
 */
export function onTrackEvent(callback: TrackEventCallback): () => void {
  trackEventCallbacks.push(callback);
  return () => {
    const index = trackEventCallbacks.indexOf(callback);
    if (index > -1) {
      trackEventCallbacks.splice(index, 1);
    }
  };
}

/**
 * Track an event in Umami Analytics
 * @param eventName - Name of the event to track
 * @param eventData - Optional data to associate with the event
 */
export function trackEvent(
  eventName?: string,
  eventData?: Record<string, unknown>,
): void {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.track(eventName, eventData);

    // Notify callbacks
    trackEventCallbacks.forEach((callback) => callback(eventName));

    const loggingEnabled = localStorage.getItem(UMAMI_DISABLED_KEY) === "true";
    if (!loggingEnabled) return;

    if (
      eventName !== AnalyticsEvents.HEARTBEAT &&
      eventName !== AnalyticsEvents.USER_IDLE &&
      eventName !== AnalyticsEvents.USER_ACTIVE &&
      eventName !== AnalyticsEvents.APP_VISIBLE &&
      eventName !== AnalyticsEvents.APP_HIDDEN
    ) {
      const path = window.location.pathname;
      let label = eventName ?? "pageview";
      label += ` ${path}`;

      if (eventData) {
        console.debug(label, eventData);
      } else {
        console.debug(label);
      }
    }
  }
}

/**
 * Identify user in Umami Analytics
 * @param userTrackingId - Unique user tracking identifier
 * @param email - User's email address
 * @param name - User's display name
 * @param userId - User's database ID (optional)
 */
export function identifyUser(
  userTrackingId: string,
  email?: string,
  name?: string,
  userId?: string,
): void {
  if (typeof window !== "undefined" && window.umami) {
    window.umami.identify(userTrackingId, {
      userId,
      email,
      name,
    });
  }
}

export function dynamicEventName(event: AnalyticsEvent, text: string): string {
  return (
    event +
    ":" +
    text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .substring(0, 50)
  );
}
